<template>
  <div class="patient-list-page-wrapper">
    <nav-bar @userPrefs="handleUserPrefs" />
    <v-container class="patient-list-page" fluid pa-0>
      <v-row no-gutters class="fill-height">
        <v-col :cols="3"  class="fill-height">
          <v-layout 
            column 
            fill-height 
            escalon-br
            :style="borderStyle" >
            <v-flex shrink>
              <patient-list-toolbar 
                @newPatient="handleNewPatient" />
            </v-flex>
            <v-flex class="patient-list-table-wrapper">
              <patient-list-table />
            </v-flex>
          </v-layout>
        </v-col>
        <v-col :cols="9" class="fill-height">
          <v-layout column fill-height>
            <v-flex shrink>
              <patient-detail-toolbar
                :style="borderStyle"
                @editPatient="handleEditPatient(patient)"
                @manualUpload="handleManualUpload(patient)"/>
            </v-flex>
            <v-flex grow patient-detail-flex-wrapper>
              <patient-detail-table @open="handleExamEdit" />
            </v-flex>
          </v-layout>
        </v-col>
      </v-row>
    </v-container>
    <delete-exam-dialog />
    <download-exam-dialog />
    <advanced-search />
    <user-prefs
      :openPrefs="isPrefs"
      :propPrefs="prefs"
      @close="closeUserPrefs"
      @submitPref="submitUserPrefs"/>
    <patient-exam-multiview-browser @manualUpload="handleManualUpload(patient)" />
    <add-edit-patient-dialog
      :isEditing="isEditingPatient"
      :isAdding="isAddingPatient"
      :isAddingExam="isAddingExam"
      :isManualUpload="isManualUpload"
      :isSaving="isSavingPatient"
      :isMergingPatient="isMergingPatient"
      :canEditPatient="canEditPatient"
      :patient="editedPatient"
      :patientsToMerge="patientsToMerge"
      @skipExam="skipExam"
      @close="closeEditPatient"
      @submit="handleSubmitEditPatient"
      @merge="handleMergePatients"/>
    <edit-exam-dialog />
    <image-browser v-if="!isIE" />
    <proofsheet-modal-browser />
    <placeholder-modal-browser />
    <stereo-browser />
    <print-preview-browser />
    <overlay-browser />
    <side-by-side-browser />
    <remove-images-dialog />
    <download-dialog />
    <mark-images-dialog />
    <change-password-dialog />
    <edit-annotation-text-dialog />
    <help-dialog />
    <video-viewer-dialog />
    <deep-dee-info-dialog />
    <shape-outline-dialog />
    <shape-fill-dialog />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import userPrefs from "../components/userPreferences/userPrefs";
import NavBar from "../components/nav/NavBar";

import PatientListToolbar from "../components/patientList/PatientListToolbar";
import PatientListTable from "../components/patientList/PatientListTable";
import PatientDetailToolbar from "../components/patientList/PatientDetailToolbar";
import PatientDetailTable from "../components/patientList/PatientDetailTable";

import DeleteExamDialog from "../components/dialogs/DeleteExamDialog";
import DownloadExamDialog from "../components/dialogs/DownloadExamDialog";

import uploadExamFiles from "../components/mixins/UploadExamFiles";
import addEditPatient from "../components/mixins/AddEditPatient";
import addEditPatientDialog from "../components/dialogs/AddEditPatientDialog";
import editExamDialog from "../components/dialogs/EditExamDialog";

import imageBrowser from "../components/imageBrowser/ImageBrowser";
import advancedSearch from "../components/nav/AdvancedSearch";

import patientExamMultiviewBrowser from "../components/multiview/PatientExamMultiviewBrowser";
import proofsheetModalBrowser from "../components/multiview/ProofsheetModalBrowser";
import OverlayBrowser from "../components/multiview/OverlayBrowser";
import placeholderModalBrowser from "../components/multiview/PlaceholderModalBrowser";
import stereoBrowser from "../components/stereo/StereoBrowser";
import printPreviewBrowser from "../components/print/PrintPreviewBrowser";
import sideBySideBrowser from "../components/sideBySide/SideBySideBrowser";
import removeImagesDialog from "../components/multiview/RemoveImagesDialog";
import downloadDialog from "../components/dialogs/DownloadDialog";
import markImagesDialog from "../components/multiview/MarkImagesDialog";
import changePasswordDialog from "../components/dialogs/ChangePasswordDialog";
import editAnnotationTextDialog from "../canvas/components/EditAnnotationTextDialog";
import helpDialog from "../components/multiview/HelpDialog";
import videoViewerDialog from "../components/video/VideoViewerDialog";
import deepDeeInfoDialog from "../components/dialogs/DeepDeeInfoDialog";
import shapeOutlineDialog from "../canvas/components/ShapeOutlineDialog";
import shapeFillDialog from "../canvas/components/ShapeFillDialog";

export default {
  components: {
    userPrefs,
    PatientListToolbar,
    PatientListTable,
    PatientDetailToolbar,
    PatientDetailTable,
    DeleteExamDialog,
    addEditPatientDialog,
    NavBar,
    imageBrowser,
    patientExamMultiviewBrowser,
    OverlayBrowser,
    proofsheetModalBrowser,
    placeholderModalBrowser,
    stereoBrowser,
    printPreviewBrowser,
    DownloadExamDialog,
    advancedSearch,
    sideBySideBrowser,
    editExamDialog,
    removeImagesDialog,
    downloadDialog,
    markImagesDialog,
    changePasswordDialog,
    editAnnotationTextDialog,
    helpDialog,
    videoViewerDialog,
    deepDeeInfoDialog,
    shapeOutlineDialog,
    shapeFillDialog
  },
  mixins: [uploadExamFiles, addEditPatient],
  data() {
    return {
      isPrefs: false,
      prefs: {
        pageSize: null,
        patientDateSetting: null,
        defaultColorTheme: null,
        fullscreens: null,
        autoPatientExamRefresh: null,
        defaultHVF: null,
        defaultHVFview: null,
        multiviewCollapse: null,
        cirrusScanDisplayRatio: null
      }
    };
  },
  computed: {
    ...mapGetters(["isIE", "patient", "patients", "examToEdit", "usrMustChangePwd", "browserStack"])
  },
  methods: {
    handleUserPrefs() {
      var userPrefs = this.$store.getters.userPrefs;
      this.prefs.pageSize = userPrefs.pageSize;
      this.prefs.patientDateSetting = userPrefs.patientDateSetting;
      this.prefs.defaultColorTheme = userPrefs.defaultColorTheme;
      this.prefs.fullscreens = userPrefs.fullscreen;
      this.prefs.autoPatientExamRefresh = userPrefs.autoPatientExamRefresh;
      this.prefs.defaultHVF = userPrefs.defaultHVF;
      this.prefs.defaultCirrusView = userPrefs.defaultCirrusView;
      this.prefs.defaultHVFview = userPrefs.defaultHVFview;
      this.prefs.multiviewCollapse = userPrefs.multiviewCollapse;
      this.prefs.cirrusScanDisplayRatio = userPrefs.cirrusScanDisplayRatio;
      this.isPrefs = true;
    },
    closeUserPrefs() {
      this.isPrefs = false;
    },
    submitUserPrefs(prefs) {
      this.prefs = prefs;
      this.isPrefs = false;
      this.$store
        .dispatch("setUserPrefs", {
          fullscreen: prefs.fullscreens,
          patientDateSetting: prefs.patientDateSetting,
          defaultColorTheme: prefs.defaultColorTheme,
          pageSize: prefs.pageSize,
          autoPatientExamRefresh: prefs.autoPatientExamRefresh,
          defaultHVF: prefs.defaultHVF,
          defaultCirrusView: prefs.defaultCirrusView,
          defaultHVFview: prefs.defaultHVFview,
          multiviewCollapse: prefs.multiviewCollapse,
          cirrusScanDisplayRatio: prefs.cirrusScanDisplayRatio
        })
        .then(() => {
          this.$store.commit("displayStatus", { message: "Successfully changed user preferences. The changes will go into effect upon next login or refreshing the page. ", level: "success" });
        })
        // todo - notify user of error?
        .catch(() => {
          this.$store.commit("displayStatus", { message: "Failed to change user preferences.", level: "error" });
        });
    },
    handleExamEdit(exam) {
      var editExam = Object.assign({}, exam);
      if (editExam.exAttdMD) editExam.exAttdMD = editExam.exAttdMD.map(String);
      else editExam.exAttdMD = [];
      if (editExam.exRefMD) editExam.exRefMD = editExam.exRefMD.map(String);
      else editExam.exRefMD = [];
      this.$store.commit("setExamToEdit", editExam);
    }
  }
};
</script>
